import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ShoppingBag, Heart, Share2, Truck, Shield, RefreshCw, ChevronRight, Minus, Plus, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AddToCartButton } from '../components/Cart';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOPIFY_STORE = 'https://gifts.charmingcreationsgifts.com';

const ProductPage = () => {
  const { handle } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      
      try {
        // Fetch all products (using high limit to get all 800+)
        const response = await axios.get(`${API}/shopify/products?limit=1000`);
        if (response.data.products) {
          const foundProduct = response.data.products.find(p => p.handle === handle);
          if (foundProduct) {
            setProduct(foundProduct);
            // Get related products with same tags
            const related = response.data.products
              .filter(p => p.handle !== handle && p.tags?.some(tag => foundProduct.tags?.includes(tag)))
              .slice(0, 4);
            setRelatedProducts(related.length > 0 ? related : response.data.products.filter(p => p.handle !== handle).slice(0, 4));
          }
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (handle) {
      fetchProduct();
    }
    window.scrollTo(0, 0);
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">Sorry, we couldn't find that product.</p>
          <Link to="/">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images || [product.image];
  const productUrl = `${SHOPIFY_STORE}/products/${product.handle}`;
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;

  // Product Schema for SEO
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "image": images,
    "description": product.description || `Handcrafted ${product.name} from Charming Creations Australia`,
    "brand": {
      "@type": "Brand",
      "name": "Charming Creations"
    },
    "offers": {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": "AUD",
      "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": `https://www.charmingcreationsgifts.com/product/${product.handle}`,
      "seller": {
        "@type": "Organization",
        "name": "Charming Creations"
      }
    }
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.charmingcreationsgifts.com" },
      { "@type": "ListItem", "position": 2, "name": "Products", "item": "https://www.charmingcreationsgifts.com/jewellery" },
      { "@type": "ListItem", "position": 3, "name": product.name, "item": `https://www.charmingcreationsgifts.com/product/${product.handle}` }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <Helmet>
        <title>{product.name} | Charming Creations Australia</title>
        <meta name="description" content={product.description?.substring(0, 160) || `Shop ${product.name} - Handcrafted resin art from Charming Creations. Free shipping Australia-wide.`} />
        <link rel="canonical" href={`https://www.charmingcreationsgifts.com/product/${product.handle}`} />
        <meta property="og:title" content={`${product.name} | Charming Creations`} />
        <meta property="og:description" content={product.description?.substring(0, 160) || `Handcrafted ${product.name}`} />
        <meta property="og:image" content={images[0]} />
        <meta property="og:type" content="product" />
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content="AUD" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <nav className="bg-white border-b border-gray-100 py-3">
        <div className="container mx-auto px-4">
          <ol className="flex items-center text-sm text-gray-500 flex-wrap">
            <li><Link to="/" className="hover:text-amber-600">Home</Link></li>
            <li className="mx-2">/</li>
            <li><Link to="/jewellery" className="hover:text-amber-600">Products</Link></li>
            <li className="mx-2">/</li>
            <li className="text-amber-600 font-medium truncate max-w-[200px]">{product.name}</li>
          </ol>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-lg font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent hidden sm:block">
                Charming Creations
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Product Section */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square rounded-2xl overflow-hidden bg-white shadow-lg">
                <img 
                  src={images[selectedImage] || images[0]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === idx ? 'border-amber-500 shadow-md' : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {hasDiscount && (
                  <Badge className="bg-red-500 text-white">{discountPercent}% OFF</Badge>
                )}
                {product.inStock ? (
                  <Badge className="bg-green-500 text-white">In Stock</Badge>
                ) : (
                  <Badge className="bg-gray-500 text-white">Sold Out</Badge>
                )}
                <Badge className="bg-amber-100 text-amber-800">Handcrafted</Badge>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{product.name}</h1>

              {/* Price */}
              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold text-amber-600">${product.price?.toFixed(2)}</span>
                {hasDiscount && (
                  <span className="text-xl text-gray-400 line-through">${product.compare_at_price?.toFixed(2)}</span>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 leading-relaxed">{product.description}</p>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <span className="text-gray-700 font-medium">Quantity:</span>
                  <div className="flex items-center border-2 border-gray-200 rounded-lg">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-2 hover:bg-gray-100 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 py-2 font-medium">{quantity}</span>
                    <button 
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-2 hover:bg-gray-100 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <AddToCartButton product={product} quantity={quantity} variant="large" className="flex-1" />
                  <a 
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full border-2 border-amber-500 text-amber-600 hover:bg-amber-50">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      View on Store
                    </Button>
                  </a>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <Truck className="w-8 h-8 mx-auto text-teal-600 mb-2" />
                  <p className="text-xs text-gray-600">Free Shipping<br/>Australia-wide</p>
                </div>
                <div className="text-center">
                  <Shield className="w-8 h-8 mx-auto text-teal-600 mb-2" />
                  <p className="text-xs text-gray-600">Secure<br/>Checkout</p>
                </div>
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 mx-auto text-teal-600 mb-2" />
                  <p className="text-xs text-gray-600">30-Day<br/>Returns</p>
                </div>
              </div>

              {/* Share */}
              <div className="flex items-center gap-4 pt-4">
                <span className="text-gray-600 text-sm">Share:</span>
                <button 
                  onClick={() => navigator.share?.({ title: product.name, url: window.location.href })}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Share2 className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((relatedProduct, idx) => (
                <Link 
                  key={idx}
                  to={`/product/${relatedProduct.handle}`}
                  className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100"
                >
                  <div className="aspect-square rounded-t-xl overflow-hidden bg-gray-50">
                    <img 
                      src={relatedProduct.image || relatedProduct.images?.[0]}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-2 group-hover:text-amber-600 transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-amber-600 font-bold mt-2">${relatedProduct.price?.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
              alt="Charming Creations Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold">Charming Creations</span>
          </Link>
          <p className="text-gray-400 text-sm">© 2025 Charming Creations. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ProductPage;
